/**
 * Validated build-time OIDC + API configuration.
 *
 * All values come from VITE_* build-time env vars (public — never secret).
 * The module validates required vars at import time so a misconfiguration fails
 * fast and loudly rather than producing a silent runtime auth failure.
 *
 * Security invariant: PKCE public client — no client secret ever exists in
 * this bundle (ARCHITECTURE §7, PRD M5).
 */

function required(key: string): string {
  const value = import.meta.env[key] as string | undefined;
  if (!value) {
    throw new Error(
      `[config] Required environment variable ${key} is missing or empty. ` +
        `Check your .env file and the VITE_* build args.`,
    );
  }
  return value;
}

function optional(key: string, fallback: string): string {
  const value = import.meta.env[key] as string | undefined;
  return value ?? fallback;
}

/**
 * Default scopes (deSjeff B2C — Zitadel is pure authentication):
 *  - openid profile email — standard OIDC identity (name / preferred_username / email)
 *  - offline_access       — refresh token from Zitadel
 *  - urn:zitadel:iam:org:project:id:zitadel:aud — the reserved "ZITADEL project"
 *    audience scope. Without it, the access token's `aud` does not include
 *    Zitadel's own API, so the "Koble til Google" account-linking calls
 *    (POST .../v2/idp_intents, .../v2/users/{sub}/links, …) made directly
 *    against Zitadel's user/v2 API — see infra/zitadel/CONNECT-GOOGLE-SPEC.md
 *    §2/§4 — would be rejected on audience. This is additive: the deSjeff API's
 *    own audience check accepts a token whose `aud` CONTAINS an accepted
 *    value, so adding this extra audience does not break `/me` or any other
 *    API call. Deliberately NOT requesting project-roles scopes — deSjeff is
 *    sub-only B2C (see CONNECT-GOOGLE-SPEC.md §7).
 *
 * There is NO org/tenant claim and NO project-roles claim: the isolation boundary
 * is the Cookbook, and per-cookbook roles are resolved server-side from membership
 * (never carried in the token).
 */
const DEFAULT_SCOPE =
  "openid profile email offline_access urn:zitadel:iam:org:project:id:zitadel:aud";

export const config = {
  oidc: {
    issuer: required("VITE_OIDC_ISSUER"),
    clientId: required("VITE_OIDC_CLIENT_ID"),
    redirectUri: required("VITE_OIDC_REDIRECT_URI"),
    postLogoutRedirectUri: required("VITE_OIDC_POST_LOGOUT_REDIRECT_URI"),
    scope: optional("VITE_OIDC_SCOPE", DEFAULT_SCOPE),
  },
  api: {
    baseUrl: required("VITE_API_BASE_URL"),
  },
  zitadel: {
    // Instance-level IdP ids (non-secret snowflakes), used only by the
    // "Koble til <provider>" account-linking flow (CONNECT-GOOGLE-SPEC.md,
    // which generalizes to any provider via identity/idpLink.ts +
    // identity/providers.ts). OPTIONAL per provider: empty ⇒ that provider's
    // linking affordance stays hidden (feature off) rather than the app
    // failing to boot — see identity/providers.ts.
    googleIdpId: optional("VITE_GOOGLE_IDP_ID", ""),
    facebookIdpId: optional("VITE_FACEBOOK_IDP_ID", ""),
  },
} as const;
